import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  Button,
  Card,
  CardTitle,
  FormField,
  Input,
  Select,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@invested/ui';
import * as api from '../../services/api';
import { ApiError } from '../../services/api';
import type { Sample, Shipment } from '../../types';
import { SHIPMENT_STATUS_LABEL } from '../participants/statusLabels';

function NewShipmentForm({ storedSamples, onCreated }: { storedSamples: Sample[]; onCreated: () => void }) {
  const [destinationLab, setDestinationLab] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await api.createShipment({ destinationLab, sampleIds: [...selected] });
      setDestinationLab('');
      setSelected(new Set());
      onCreated();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Kunne ikke opprette forsendelse.');
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <FormField label="Mottakslaboratorium" htmlFor="destinationLab" required>
        <Input id="destinationLab" required value={destinationLab} onChange={(e) => setDestinationLab(e.target.value)} />
      </FormField>
      <div>
        <p className="mb-2 text-sm font-medium">Velg prøver som skal med ({selected.size} valgt)</p>
        <div className="max-h-56 space-y-1 overflow-y-auto rounded-xl border border-black/10 p-2">
          {storedSamples.length === 0 && <p className="p-2 text-sm text-black/45">Ingen lagrede prøver tilgjengelig.</p>}
          {storedSamples.map((sample) => (
            <label key={sample.id} className="flex items-center gap-2 rounded-lg px-2 py-1 text-sm hover:bg-black/5">
              <input type="checkbox" checked={selected.has(sample.id)} onChange={() => toggle(sample.id)} />
              {sample.participant?.pseudonymId} — {sample.cryoLabelId}
            </label>
          ))}
        </div>
      </div>
      {error && <p className="text-sm text-red-700">{error}</p>}
      <Button type="submit" disabled={pending || selected.size === 0}>
        {pending ? 'Oppretter…' : 'Opprett forsendelse'}
      </Button>
    </form>
  );
}

function ShipmentRow({ shipment, onUpdated }: { shipment: Shipment; onUpdated: () => void }) {
  const [pending, setPending] = useState(false);

  async function markSent() {
    setPending(true);
    try {
      await api.updateShipment(shipment.id, { status: 'SENT', sentDate: new Date().toISOString() });
      onUpdated();
    } finally {
      setPending(false);
    }
  }

  async function markReceived() {
    setPending(true);
    try {
      await api.updateShipment(shipment.id, { status: 'RECEIVED', receiptConfirmedAt: new Date().toISOString() });
      onUpdated();
    } finally {
      setPending(false);
    }
  }

  async function markIdatReceived() {
    setPending(true);
    try {
      await api.updateShipment(shipment.id, { status: 'IDAT_RECEIVED', idatReceivedDate: new Date().toISOString() });
      onUpdated();
    } finally {
      setPending(false);
    }
  }

  return (
    <TableRow>
      <TableCell>{shipment.destinationLab}</TableCell>
      <TableCell>
        <StatusPill tone={shipment.status === 'IDAT_RECEIVED' ? 'ok' : shipment.status === 'PREPARING' ? 'unknown' : 'info'}>
          {SHIPMENT_STATUS_LABEL[shipment.status]}
        </StatusPill>
      </TableCell>
      <TableCell>{shipment.samples.length}</TableCell>
      <TableCell>{shipment.sentDate ? shipment.sentDate.slice(0, 10) : '—'}</TableCell>
      <TableCell>{shipment.idatReceivedDate ? shipment.idatReceivedDate.slice(0, 10) : '—'}</TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-2">
          {shipment.status === 'PREPARING' && (
            <Button size="sm" variant="secondary" disabled={pending} onClick={markSent}>
              Marker sendt
            </Button>
          )}
          {shipment.status === 'SENT' && (
            <Button size="sm" variant="secondary" disabled={pending} onClick={markReceived}>
              Marker mottatt
            </Button>
          )}
          {shipment.status === 'RECEIVED' && (
            <Button size="sm" variant="secondary" disabled={pending} onClick={markIdatReceived}>
              Marker IDAT mottatt
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

export function ShipmentsPage() {
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [storedSamples, setStoredSamples] = useState<Sample[]>([]);

  const load = useCallback(() => {
    api.getShipments().then(setShipments).catch(() => undefined);
    api.getSamples('STORED').then(setStoredSamples).catch(() => undefined);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6">
      <CardTitle as="h1">Forsendelser</CardTitle>

      <Card size="lg">
        <CardTitle as="h2">Ny forsendelse</CardTitle>
        <div className="mt-4">
          <NewShipmentForm storedSamples={storedSamples} onCreated={load} />
        </div>
      </Card>

      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>Mottakslab</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
            <TableHeaderCell>Prøver</TableHeaderCell>
            <TableHeaderCell>Sendt</TableHeaderCell>
            <TableHeaderCell>IDAT mottatt</TableHeaderCell>
            <TableHeaderCell>Handling</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shipments.map((shipment) => (
            <ShipmentRow key={shipment.id} shipment={shipment} onUpdated={load} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
